import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, Link2 } from 'lucide-react';
import { ColorOption } from '../../types/interiorConfig';
import { useTheme } from '../styles/ThemeContext';
type ColorSelectMode = 'bar' | 'radial';
interface ColorSwatchPopoverProps {
  colorOptions: ColorOption[];
  selectedColorId: string | null;
  onSelectColor: (colorId: string) => void;
  optionName: string;
}
function isLightColor(hex: string): boolean {
  const h = hex.toLowerCase();
  return h === '#f5f5f5' || h === '#ffffff' || h === '#d4c5a9' || h === '#b8b8b8' || h === '#c0c0c0';
}
// ── Bar Mode: Horizontal pill with label + swatches + color name ──
function BarMode({
  colorOptions,
  effectiveSelectedId,
  onSelectColor,
  onSwitchMode,
  optionName,
  isDark
}: {
  colorOptions: ColorOption[];
  effectiveSelectedId: string;
  onSelectColor: (id: string) => void;
  onSwitchMode: () => void;
  optionName: string;
  isDark: boolean;
}) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const hoveredColor = colorOptions.find(c => c.id === hoveredId);
  const selectedColor = colorOptions.find(c => c.id === effectiveSelectedId);
  const displayName = hoveredColor?.name || selectedColor?.name || '';
  return <motion.div initial={{
    opacity: 0,
    y: 8,
    scale: 0.97
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: 8,
    scale: 0.97
  }} transition={{
    duration: 0.2,
    ease: [0.22, 1, 0.36, 1]
  }} className={`flex items-center gap-4 px-5 py-2.5 rounded-full backdrop-blur-xl border shadow-2xl ${isDark ? 'bg-slate-800/90 border-white/10' : 'bg-white/90 border-gray-200 shadow-lg'}`} onClick={e => e.stopPropagation()} data-id="element-591">
      {/* Label */}
      <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-white/50' : 'text-gray-400'}`} data-id="element-592">
        {optionName} Color
      </span>

      {/* Divider */}
      <div className={`w-px h-5 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-593" />

      {/* Swatches */}
      <div className="flex items-center gap-2" data-id="element-594">
        {colorOptions.map((color, i) => {
        const isSelected = color.id === effectiveSelectedId;
        const light = isLightColor(color.hex);
        return <motion.button key={color.id} initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: i * 0.04,
          duration: 0.2
        }} onClick={e => {
          e.stopPropagation();
          onSelectColor(color.id);
        }} onMouseEnter={() => setHoveredId(color.id)} onMouseLeave={() => setHoveredId(null)} className={`relative w-8 h-8 rounded-full transition-all duration-200 ${isSelected ? 'scale-110' : 'hover:scale-110'}`} style={{
          backgroundColor: color.hex
        }} aria-label={`Select ${color.name}`} data-id="element-595">
              {/* Ring for selected */}
              {isSelected && <div className={`absolute -inset-[3px] rounded-full border-2 ${isDark ? 'border-cyan-400' : 'border-teal-500'}`} data-id="element-596" />}
              {/* Border for light colors */}
              {light && !isSelected && <div className={`absolute inset-0 rounded-full border ${isDark ? 'border-white/20' : 'border-gray-300'}`} data-id="element-597" />}
              {/* Check */}
              {isSelected && <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} className="absolute inset-0 flex items-center justify-center" data-id="element-598">
                  <Check className={`w-4 h-4 ${light ? 'text-gray-700' : 'text-white'}`} strokeWidth={2.5} data-id="element-599" />
                </motion.div>}
            </motion.button>;
      })}
      </div>

      {/* Color name */}
      <AnimatePresence mode="wait" data-id="element-600">
        <motion.span key={displayName} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.1
      }} className={`text-xs font-medium whitespace-nowrap min-w-[60px] ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-601">
          {displayName}
        </motion.span>
      </AnimatePresence>

      {/* Mode switch button */}
      <button onClick={e => {
      e.stopPropagation();
      onSwitchMode();
    }} className={`p-1.5 rounded-full transition-colors ${isDark ? 'text-white/30 hover:text-white/60 hover:bg-white/5' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`} title="Switch to radial view" data-id="element-602">
        <Sparkles className="w-4 h-4" data-id="element-603" />
      </button>
    </motion.div>;
}
// ── Radial Mode: Semi-circular arc of swatches ──
function RadialMode({
  colorOptions,
  effectiveSelectedId,
  onSelectColor,
  onSwitchMode,
  isDark
}: {
  colorOptions: ColorOption[];
  effectiveSelectedId: string;
  onSelectColor: (id: string) => void;
  onSwitchMode: () => void;
  isDark: boolean;
}) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const hoveredColor = colorOptions.find(c => c.id === hoveredId);
  const selectedColor = colorOptions.find(c => c.id === effectiveSelectedId);
  const displayName = hoveredColor?.name || selectedColor?.name || '';
  const count = colorOptions.length;
  // Arc spans from -60° to +60° (120° total), centered at top
  const arcStart = -60;
  const arcEnd = 60;
  const arcSpan = arcEnd - arcStart;
  const radius = count <= 3 ? 60 : count <= 5 ? 70 : 80;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.9
  }} transition={{
    duration: 0.25,
    ease: [0.22, 1, 0.36, 1]
  }} className="flex flex-col items-center gap-1" onClick={e => e.stopPropagation()} data-id="element-604">
      {/* Color name label */}
      <AnimatePresence mode="wait" data-id="element-605">
        <motion.span key={displayName} initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.12
      }} className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-606">
          {displayName}
        </motion.span>
      </AnimatePresence>

      {/* Arc container */}
      <div className="relative" style={{
      width: radius * 2 + 48,
      height: radius + 32
    }} data-id="element-607">
        {colorOptions.map((color, i) => {
        const isSelected = color.id === effectiveSelectedId;
        const light = isLightColor(color.hex);
        // Calculate position on arc
        const angle = count === 1 ? 0 : arcStart + arcSpan / (count - 1) * i;
        const rad = angle * Math.PI / 180;
        // Arc center is at bottom-center of the container
        const cx = radius + 24;
        const cy = radius + 8;
        const x = cx + radius * Math.sin(rad);
        const y = cy - radius * Math.cos(rad);
        return <motion.button key={color.id} initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1,
          x: x - 20,
          y: y - 20
        }} transition={{
          delay: i * 0.05,
          duration: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} onClick={e => {
          e.stopPropagation();
          onSelectColor(color.id);
        }} onMouseEnter={() => setHoveredId(color.id)} onMouseLeave={() => setHoveredId(null)} className={`absolute w-10 h-10 rounded-full transition-transform duration-200 ${isSelected ? 'scale-110 z-10' : 'hover:scale-115'}`} style={{
          backgroundColor: color.hex
        }} aria-label={`Select ${color.name}`} data-id="element-608">
              {/* Ring for selected */}
              {isSelected && <div className={`absolute -inset-[3px] rounded-full border-2 ${isDark ? 'border-cyan-400' : 'border-teal-500'}`} data-id="element-609" />}
              {/* Border for light colors */}
              {light && !isSelected && <div className={`absolute inset-0 rounded-full border ${isDark ? 'border-white/20' : 'border-gray-300'}`} data-id="element-610" />}
              {/* Shadow for depth */}
              <div className="absolute inset-0 rounded-full shadow-lg" data-id="element-611" />
              {/* Check */}
              {isSelected && <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} className="absolute inset-0 flex items-center justify-center" data-id="element-612">
                  <Check className={`w-5 h-5 ${light ? 'text-gray-700' : 'text-white'}`} strokeWidth={2.5} data-id="element-613" />
                </motion.div>}
            </motion.button>;
      })}
      </div>

      {/* Mode switch button */}
      <button onClick={e => {
      e.stopPropagation();
      onSwitchMode();
    }} className={`p-2 rounded-full backdrop-blur-md border transition-colors ${isDark ? 'bg-slate-800/80 border-white/10 text-white/40 hover:text-white/70 hover:bg-slate-800' : 'bg-white/80 border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-white'}`} title="Switch to bar view" data-id="element-614">
        <Link2 className="w-3.5 h-3.5" data-id="element-615" />
      </button>
    </motion.div>;
}
// ── Main Export ──
export function ColorSwatchPopover({
  colorOptions,
  selectedColorId,
  onSelectColor,
  optionName
}: ColorSwatchPopoverProps) {
  const {
    isDark
  } = useTheme();
  const [mode, setMode] = useState<ColorSelectMode>('bar');
  if (!colorOptions || colorOptions.length <= 1) return null;
  const effectiveSelectedId = selectedColorId || colorOptions.find(c => c.selected)?.id || colorOptions[0].id;
  const switchMode = () => setMode(prev => prev === 'bar' ? 'radial' : 'bar');
  return <AnimatePresence mode="wait" data-id="element-616">
      {mode === 'bar' ? <BarMode key="bar" colorOptions={colorOptions} effectiveSelectedId={effectiveSelectedId} onSelectColor={onSelectColor} onSwitchMode={switchMode} optionName={optionName} isDark={isDark} data-id="element-617" /> : <RadialMode key="radial" colorOptions={colorOptions} effectiveSelectedId={effectiveSelectedId} onSelectColor={onSelectColor} onSwitchMode={switchMode} isDark={isDark} data-id="element-618" />}
    </AnimatePresence>;
}